import { useState, useEffect, useCallback, useMemo } from 'react';

interface Transaction {
    id: string;
    entity_id: number;
    amount: string;
    description: string;
    date: string;
    type?: string;
    accountId: number;
}

interface PersistentTransactionSelection {
    [accountId: string]: Transaction[]; // Use string keys for Plaid IDs
}

// ✅ Phase 4: Optimized selection tracking with Set-based lookups
interface SelectionLookup {
    [accountId: string]: Set<number>; // entity_id sets for O(1) lookups, use string keys for Plaid IDs
}

const STORAGE_KEY = 'import-transactions-selections';

function usePersistentTransactionSelection() {
    const [selections, setSelections] = useState<PersistentTransactionSelection>({});
    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

    // ✅ Phase 4: Optimized lookup with Set-based entity_id tracking - Use string keys for Plaid IDs
    const selectionLookup = useMemo<SelectionLookup>(() => {
        const lookup: SelectionLookup = {};
        Object.entries(selections).forEach(([accountId, transactions]) => {
            lookup[accountId] = new Set(transactions.map((t) => t.entity_id));
        });
        return lookup;
    }, [selections]);

    // Load selections from localStorage on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                setSelections(parsed);
            }
        } catch (error) {
            // Failed to load transaction selections from localStorage
        } finally {
            // Mark initial load as complete to allow saving
            setIsInitialLoadComplete(true);
        }
    }, []);

    // Save selections to localStorage whenever they change (but only after initial load)
    useEffect(() => {
        // Don't save until initial load is complete to prevent race condition
        if (!isInitialLoadComplete) {
            return;
        }

        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(selections));
        } catch (error) {
            // Failed to save transaction selections to localStorage
        }
    }, [selections, isInitialLoadComplete]);

    const setAccountSelections = useCallback((accountId: string, transactions: Transaction[]) => {
        setSelections((prev) => ({
            ...prev,
            [accountId]: transactions,
        }));
    }, []);

    // ✅ Phase 2: Use entity_id for consistent transaction identification - Use Plaid ID
    const addTransactionToAccount = useCallback((accountId: string, transaction: Transaction) => {
        setSelections((prev) => {
            const currentSelections = prev[accountId] || [];
            const exists = currentSelections.some((t) => t.entity_id === transaction.entity_id);

            if (exists) {
                return prev; // Already selected
            }

            return {
                ...prev,
                [accountId]: [...currentSelections, transaction],
            };
        });
    }, []);

    // ✅ Phase 2: Use entity_id for transaction removal - Use Plaid ID
    const removeTransactionFromAccount = useCallback((accountId: string, entityId: number) => {
        setSelections((prev) => {
            const currentSelections = prev[accountId] || [];
            const filtered = currentSelections.filter((t) => t.entity_id !== entityId);

            if (filtered.length === 0) {
                // Remove account entry if no selections remain
                const { [accountId]: removed, ...remaining } = prev;
                return remaining;
            }

            return {
                ...prev,
                [accountId]: filtered,
            };
        });
    }, []);

    // ✅ Phase 2: Use entity_id as primary key for reliable matching - Use Plaid ID
    const toggleTransactionInAccount = useCallback((accountId: string, transaction: Transaction) => {
        setSelections((prev) => {
            const currentSelections = prev[accountId] || [];
            const exists = currentSelections.some((t) => t.entity_id === transaction.entity_id);

            if (exists) {
                // Remove transaction
                const filtered = currentSelections.filter((t) => t.entity_id !== transaction.entity_id);

                if (filtered.length === 0) {
                    // Remove account entry if no selections remain
                    const { [accountId]: removed, ...remaining } = prev;
                    return remaining;
                }

                return {
                    ...prev,
                    [accountId]: filtered,
                };
            }
            // Add transaction
            return {
                ...prev,
                [accountId]: [...currentSelections, transaction],
            };
        });
    }, []);

    const clearAccountSelections = useCallback((accountId: string) => {
        setSelections((prev) => {
            const { [accountId]: removed, ...remaining } = prev;
            return remaining;
        });
    }, []);

    const clearAllSelections = useCallback(() => {
        setSelections({});
        localStorage.removeItem(STORAGE_KEY);
    }, []);

    const getAccountSelections = useCallback(
        (accountId: string): Transaction[] => {
            return selections[accountId] || [];
        },
        [selections]
    );

    const getAllSelections = useCallback((): Transaction[] => {
        return Object.values(selections).flat();
    }, [selections]);

    const getTotalSelectionCount = useCallback((): number => {
        const count = Object.values(selections).reduce((total, transactions) => total + transactions.length, 0);
        return count;
    }, [selections]);

    const getTotalNetExpenses = useCallback((): number => {
        const total = Object.values(selections).reduce((sum, transactions) => {
            return (
                sum +
                transactions.reduce((accountSum, transaction) => {
                    // Parse amount string to number (amount is stored as string)
                    const amount = parseFloat(transaction.amount) || 0;
                    // Use absolute value since negative amounts (debits) are positive expenses
                    return accountSum + Math.abs(amount);
                }, 0)
            );
        }, 0);
        return total;
    }, [selections]);

    // This is informational only and default tax rate is used to calculate the estimated deductions
    const getEstimatedDeductions = useCallback((): number => {
        const netExpenses = getTotalNetExpenses();
        // 30% = 22% federal + 8% state tax rates
        const taxRate = 0.3;
        return netExpenses * taxRate;
    }, [getTotalNetExpenses]);

    const hasSelections = useCallback((): boolean => {
        return Object.keys(selections).length > 0;
    }, [selections]);

    // ✅ Phase 4: O(1) lookup using Set - Use Plaid ID
    const hasAccountSelections = useCallback(
        (accountId: string): boolean => {
            return (selectionLookup[accountId]?.size || 0) > 0;
        },
        [selectionLookup]
    );

    // ✅ Phase 4: O(1) transaction selection check - Use Plaid ID
    const isTransactionSelected = useCallback(
        (accountId: string, entityId: number): boolean => {
            return selectionLookup[accountId]?.has(entityId) || false;
        },
        [selectionLookup]
    );

    // ✅ Phase 4: Batch selection operations - Use Plaid ID
    const setAccountSelectionsFromEntityIds = useCallback(
        (accountId: string, entityIds: number[], allTransactions: Transaction[]) => {
            const transactionMap = new Map(allTransactions.map((t) => [t.entity_id, t]));
            const selectedTransactions = entityIds
                .map((entityId) => transactionMap.get(entityId))
                .filter((t): t is Transaction => t !== undefined);

            setSelections((prev) => ({
                ...prev,
                [accountId]: selectedTransactions,
            }));
        },
        []
    );

    // Force reload from localStorage
    const reloadFromStorage = useCallback(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                setSelections(parsed);
            } else {
                setSelections({});
            }
        } catch (error) {
            // Failed to reload from localStorage
            setSelections({});
        }
    }, []);

    return {
        // State
        selections,

        // Actions
        setAccountSelections,
        addTransactionToAccount,
        removeTransactionFromAccount,
        toggleTransactionInAccount,
        clearAccountSelections,
        clearAllSelections,

        // Getters
        getAccountSelections,
        getAllSelections,
        getTotalSelectionCount,
        getTotalNetExpenses,
        getEstimatedDeductions,
        hasSelections,
        hasAccountSelections,

        // ✅ Phase 4: Optimized operations
        isTransactionSelected,
        setAccountSelectionsFromEntityIds,

        // ✅ Force reload capability
        reloadFromStorage,
    };
}

export default usePersistentTransactionSelection;
